import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { DateField } from '@astro/date-field'
import { formatTimeLabel, toIsoDate } from '../../../utils/date'
import { SectionCard } from './DetailBits'

/** { status, duration, rate, slots: [...] } — tolerate a bare array too. */
function unwrapSlots(response) {
  if (!response) return []
  if (Array.isArray(response)) return response
  if (Array.isArray(response.slots)) return response.slots
  if (Array.isArray(response.data)) return response.data
  return []
}

/**
 * Pick-a-new-slot panel used by both detail pages. Reads free slots the same
 * way the booking calendar does (GET /available-slots) and submits
 * POST /booking/reschedule with the chosen slot's id — the upstream contract
 * (verified against the legacy portal) is { booking_id, date, slot_id }.
 * On success the booking-details query is invalidated so the page re-renders
 * with the new schedule.
 */
export default function ReschedulePanel({ booking, onDone, onError }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState('')
  const [slot, setSlot] = useState(null) // { id, start_time }

  const astrologerId = booking?.astrologer_id ?? booking?.astrologer?.id
  // A gemstone consultation can only move to another gemstone slot.
  const isGemstone = booking?.category === 'gemstone'

  const { data, isPending, isError } = useQuery({
    queryKey: isGemstone ? ['gemstone-slots', date] : ['available-slots', astrologerId, date],
    queryFn: ({ signal }) =>
      isGemstone
        ? api.bookings.gemstoneSlots({ date }, { signal })
        : api.bookings.availableSlots({ astrologer_id: astrologerId, date }, { signal }),
    enabled: open && (isGemstone || Boolean(astrologerId)) && Boolean(date),
    retry: false,
  })
  const slots = unwrapSlots(data)

  const rescheduleMutation = useMutation({
    mutationFn: () => api.bookings.reschedule({ booking_id: booking.id, date, slot_id: slot.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-details', String(booking.id)] })
      setOpen(false)
      setDate('')
      setSlot(null)
      onDone?.()
    },
    onError: (err) => onError?.(err?.error?.message || err?.message || 'Could not reschedule. Please try again.'),
  })

  if (!booking) return null

  return (
    <SectionCard title="Reschedule" icon="🗓">
      {!open ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#8B7355]">Need a different time? Pick any free slot on the astrologer&rsquo;s calendar.</p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] border border-[#C8922A]/40 text-[#C8922A] hover:bg-[#C8922A]/10 transition-colors"
          >
            Pick a new slot
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] block mb-1.5">New date</span>
              <DateField
                value={date}
                min={toIsoDate(new Date())}
                onChange={(e) => { setDate(e.target.value); setSlot(null) }}
                className="rounded-lg border border-[#C8922A]/30 bg-white px-4 py-2.5 text-sm text-[#1C140A] focus:outline-none focus:border-[#C8922A]"
              />
            </label>
            <button
              type="button"
              onClick={() => { setOpen(false); setSlot(null) }}
              className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] hover:text-[#C8922A] transition-colors pb-3"
            >
              Cancel
            </button>
          </div>

          {date && isPending && <p className="text-xs text-[#8B7355] animate-pulse">Loading free slots…</p>}
          {date && isError && <p className="text-xs text-red-500">Couldn&rsquo;t load slots for this date — try another day.</p>}
          {date && !isPending && !isError && slots.length === 0 && (
            <p className="text-xs text-[#8B7355] italic">No slots available on this date.</p>
          )}
          {date && !isPending && slots.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {slots.map((s, i) => {
                const slotId = s.id ?? s.slot_id
                const label = formatTimeLabel(String(s.start_time ?? '').slice(0, 5))
                const active = slot && String(slot.id) === String(slotId)
                return (
                  <button
                    key={slotId ?? `${label}-${i}`}
                    type="button"
                    disabled={slotId == null}
                    onClick={() => setSlot({ id: slotId, start_time: s.start_time })}
                    className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors disabled:opacity-40 ${
                      active
                        ? 'bg-[#C8922A] border-[#C8922A] text-white'
                        : 'border-[#C8922A]/25 text-[#1C140A] hover:border-[#C8922A] hover:text-[#C8922A]'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          )}

          {slot && (
            <button
              type="button"
              disabled={rescheduleMutation.isPending}
              onClick={() => rescheduleMutation.mutate()}
              className="hero-btn-primary inline-flex items-center gap-2 rounded-full px-7 py-3 text-[10px] font-bold uppercase tracking-[0.2em] disabled:opacity-50"
            >
              {rescheduleMutation.isPending
                ? 'Rescheduling…'
                : `Confirm · ${formatTimeLabel(String(slot.start_time ?? '').slice(0, 5))}`}
            </button>
          )}
        </div>
      )}
    </SectionCard>
  )
}
